import {
    Box,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    useTheme,
} from '@mui/material';
import { Check, ChevronDown, Target } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import {
    COURSE_STAGE_VALUES,
    courseStageColorKey,
    courseStageLabelId,
    type CourseStage,
} from '../model';
import { QuietButton } from './PatientListButtons';

type CourseStagePickerProps = {
    stage: CourseStage;
    done: readonly CourseStage[];
    onChange: (change: {
        stage: CourseStage;
        done: readonly CourseStage[];
    }) => void;
    disabled?: boolean;
};

/**
 * Sets the patient's course stage: where the course is, and nothing else.
 * Marking a stage finished is the strip's job — its pebbles are the checklist
 * — so picking a stage here never claims that the ones it passed were done.
 *
 * The stage is book-keeping: it locks nothing, gates nothing, and is never
 * derived from the patient's datasets. That is the model, not something the
 * menu explains — a clinician reads the stage names and the marks, and copy
 * about what the system does not infer would only be noise.
 */
export default function CourseStagePicker({
    stage,
    done,
    onChange,
    disabled = false,
}: CourseStagePickerProps) {
    const intl = useIntl();
    const theme = useTheme();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    const label = (value: CourseStage) =>
        intl.formatMessage({ id: courseStageLabelId(value) });

    const close = () => setAnchor(null);
    const pick = (picked: CourseStage) => {
        close();
        onChange({ stage: picked, done });
    };

    return (
        <>
            <QuietButton
                disabled={disabled}
                onClick={(event) => setAnchor(event.currentTarget)}
                aria-haspopup="menu"
                aria-expanded={anchor !== null}
                aria-label={intl.formatMessage(
                    { id: 'patientList.stage.controlLabel' },
                    { stage: label(stage) }
                )}
                title={intl.formatMessage({ id: 'patientList.stage.change' })}
            >
                <Box
                    component="span"
                    sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor:
                            theme.palette.courseStage[
                                courseStageColorKey(stage)
                            ],
                    }}
                />
                {label(stage)}
                <ChevronDown size={11} />
            </QuietButton>
            <Menu
                open={anchor !== null}
                anchorEl={anchor}
                onClose={close}
                MenuListProps={{ dense: true }}
            >
                {COURSE_STAGE_VALUES.map((value) => {
                    const isCurrent = value === stage;
                    const isDone = done.includes(value);
                    return (
                        <MenuItem
                            key={value}
                            selected={isCurrent}
                            onClick={() => pick(value)}
                        >
                            <ListItemIcon>
                                {isCurrent ? (
                                    <Target />
                                ) : isDone ? (
                                    <Check />
                                ) : null}
                            </ListItemIcon>
                            <ListItemText
                                primary={label(value)}
                                secondary={
                                    isCurrent
                                        ? intl.formatMessage({
                                              id: 'patientList.stage.current',
                                          })
                                        : isDone
                                          ? intl.formatMessage({
                                                id: 'patientList.stage.done',
                                            })
                                          : undefined
                                }
                            />
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
}
